
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useToast } from '@/components/ui/use-toast';
import { supabase, isSupabaseConfigured } from '@/lib/supabase';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Link, useNavigate } from 'react-router-dom';
import { Info } from 'lucide-react';
import { useAuth } from '@/contexts/MyContext';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [debugInfo, setDebugInfo] = useState<string | null>(null);
  const { toast } = useToast();
  const navigate=useNavigate();
  const {user, forgotPassword} = useAuth();

  const validateEmail = (email: string) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };
  if(user){
    navigate('/');
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email.trim()) {
      toast({
        title: "Error",
        description: "Please enter your email address",
        variant: "destructive",
      });
      return;
    }

    if (!validateEmail(email.trim())) {
      toast({
        title: "Error",
        description: "Please enter a valid email address",
        variant: "destructive",
      });
      return;
    }
    
    // Check if Supabase is configured
    if (!isSupabaseConfigured()) {
      toast({
        title: "Configuration Error",
        description: "Email functionality is not available. Supabase environment variables are missing.",
        variant: "destructive",
      });
      return;
    }
    
    setIsSubmitting(true);
    setDebugInfo(null);
    try {
      await forgotPassword(email);
      toast({
        title: "Success",
        description: "Password reset email sent successfully",
        variant: "default",
      })
    } catch (error) {
      console.log(error)
    } finally{
      setIsSubmitting(false)
    }
    // try {
    //   // Get the full URL for the reset password page
    //   const resetUrl = new URL('/reset-password', window.location.origin).toString();
    //   console.log('Reset URL:', resetUrl);
      
    //   // Use Supabase to send a password reset email
    //   // const { data, error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
    //   //   redirectTo: resetUrl,
    //   // });

      
    //   // console.log('Password reset response:', { data, error });
      
    //   // if (error) {
    //   //   throw error;
    //   // }
      
    //   const userPasswords = localStorage.getItem("userPasswords")
    //   if(userPasswords===''){
    //     toast({
    //       title: "Error",
    //       description: "Please enter a valid email address",
    //       variant: "destructive",
    //     })
    //     return;
    //   }
    //   let password = JSON.parse(userPasswords);
    //   if(!password[email.trim()]){
    //     toast({
    //       title: "Error",
    //       description: "Please enter a valid email address",
    //       variant: "destructive",
    //     })
    //     return;
    //   }
      
    //   setIsSubmitted(true);
      
    //   // For security reasons, always show the same message whether the account exists or not
    //   toast({
    //     title: "Reset link sent",
    //     description: "If an account exists with this email, you will receive a password reset link",
    //   });
    //   navigate('/reset-password?'+ email.trim())
    //   // Set debug info for development
    //   setDebugInfo(`Email sent to: ${email.trim()}. Check your inbox (including spam folder).`);
    // } catch (error) {
    //   console.error('Password reset email error:', error);
    //   let errorMessage = "There was a problem sending the reset email.";
      
    //   if (error instanceof Error) {
    //     errorMessage = error.message;
    //     setDebugInfo(`Error: ${error.message}`);
    //   }
      
    //   toast({
    //     title: "Email Sending Failed",
    //     description: errorMessage,
    //     variant: "destructive",
    //   });
    // } finally {
    //   setIsSubmitting(false);
    // }
  };

  return (
    <div className="container mx-auto px-4 py-16 max-w-md">
      <div className="bg-white shadow-md rounded-lg p-8 border border-gray-200">
        <h1 className="text-2xl font-bold text-center mb-6">Reset Your Password</h1>
        
        {!isSupabaseConfigured() && (
          <Alert className="mb-4 bg-red-50 border-red-100">
            <AlertDescription className="text-red-800 flex gap-2 items-start">
              <Info className="h-5 w-5 mt-0.5 flex-shrink-0" />
              <span>
                <strong>Configuration Error:</strong> Supabase environment variables are not set. 
                Password reset emails cannot be sent until VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY 
                are configured.
              </span>
            </AlertDescription>
          </Alert>
        )}
        
        {debugInfo && (
          <Alert className="mb-4 bg-blue-50 border-blue-100">
            <AlertDescription className="text-blue-800">
              {debugInfo}
            </AlertDescription>
          </Alert>
        )}
        
        {isSubmitted ? (
          <div className="text-left">
            <Alert className="mb-4 bg-green-50 border-green-100">
              <AlertDescription className="text-green-600">
                We've sent a password reset link to <strong>{email}</strong>. Please check your inbox (and spam folder) and follow the instructions to reset your password.
              </AlertDescription>
            </Alert>
            <p className="mb-6 text-gray-600 text-left">
              Didn't receive an email? Check your spam folder or try again.
            </p>
            <Button
              onClick={() => setIsSubmitted(false)}
              variant="outline"
              className="w-full"
            >
              Try Again
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="mb-6">
              <p className="mb-4 text-gray-600">
                Enter the email address associated with your account, and we'll send you a link to reset your password.
              </p>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                Email Address
              </label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                required
                className="w-full"
              />
            </div>
            
            <Button
              type="submit"
              className="w-full"
              disabled={isSubmitting || !isSupabaseConfigured()}
            >
              {isSubmitting ? "Sending..." : "Send Reset Link"}
            </Button>
            
            <div className="mt-4 text-center">
              <Link to="/login" className="text-sm text-blue-600 hover:underline">
                Back to Login
              </Link>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default ForgotPassword;
